import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { DEFAULT_MODALITIES, getModalityDisplayLabel, withOtherPptExtensions } from "@/lib/modalities";
import { getStoredToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ModalitiesContext = createContext(null);

const withDisplayLabels = (list) =>
  list.map((m) => ({
    ...m,
    label: getModalityDisplayLabel(m.key, m.label),
    accepted_ext: withOtherPptExtensions(m.key, m.accepted_ext),
  }));

export function ModalitiesProvider({ children }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [modalities, setModalities] = useState(() => withDisplayLabels(DEFAULT_MODALITIES));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return undefined;

    if (!isAuthenticated) {
      setModalities(withDisplayLabels(DEFAULT_MODALITIES));
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const token = getStoredToken();
    axios
      .get(`${API}/ref/modalities`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.modalities;
        if (Array.isArray(list) && list.length > 0) {
          setModalities(
            withDisplayLabels(
              list.map((m) => ({
                key: m.key,
                label: m.label,
                view_mode: m.view_mode,
                export_video: Boolean(m.export_video),
                accepted_ext: Array.isArray(m.accepted_ext) ? m.accepted_ext : [],
                size_max_mb: m.size_max_mb ?? 0,
                dicom_structure: m.dicom_structure ?? null,
              })),
            ),
          );
        }
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message ?? "Failed to load modalities");
        setModalities(withDisplayLabels(DEFAULT_MODALITIES));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [isAuthenticated, authLoading]);

  const getModality = useCallback(
    (key) => modalities.find((m) => m.key === key),
    [modalities],
  );

  const value = useMemo(
    () => ({ modalities, loading, error, getModality }),
    [modalities, loading, error, getModality],
  );

  return (
    <ModalitiesContext.Provider value={value}>
      {children}
    </ModalitiesContext.Provider>
  );
}

export function useModalities() {
  const ctx = useContext(ModalitiesContext);
  if (!ctx) throw new Error("useModalities must be used within ModalitiesProvider");
  return ctx;
}
